import { API } from '@constants'
import { serverUrl } from '@utils/MkEnv'
import { normalizeApiError } from '@utils/errorNormalizer'

/* ===================================================================
 * Registration API Facade
 * -------------------------------------------------------------------
 * Centralises calls made across the multi-step registration flow so
 * UI code and the provider don’t construct fetch requests directly.
 * =================================================================== */

export interface StepOnePayload {
  membershipNumber: string
  firstName?: string
  lastName?: string
  companyName?: string
  suffix?: string
  postalCode?: string
  contractNumber?: string
  phoneNumber?: string
  registerAsCompany: boolean
  verificationMethod: 'phone' | 'contract'
}

export async function stepOne(payload: StepOnePayload, brand: string) {
  const response = await fetch(`${serverUrl}${API}/account/lookup`, {
    method: 'POST',
    headers: {
      accept: 'application/json',
      'Content-Type': 'application/json',
      brand,
    },
    body: JSON.stringify(payload),
  })

  if (!response.ok) {
    // Parse JSON or text. Normalize voyager-embedded details to a clear message.
    try {
      const contentType = response.headers.get('content-type') || ''
      let parsed: any
      if (contentType.includes('application/json')) {
        parsed = await response.json()
      } else {
        parsed = await response.text()
      }
      const norm = normalizeApiError(parsed)
      const error = new Error(norm.message || `HTTP ${response.status}`) as Error & { voyager?: unknown; errors?: any[]; code?: string }
      if (norm.voyager) (error as any).voyager = norm.voyager
      if (norm.errors) (error as any).errors = norm.errors
      if (norm.code) (error as any).code = norm.code
      throw error
    } catch (e) {
      if (e instanceof Error) throw e
      throw new Error(`HTTP ${response.status}`)
    }
  }

  return response.json()
}

/* Username availability check */
export interface UsernameAvailabilityResponse {
  available: boolean
  suggestions?: string[]
}

export async function checkUsernameAvailability(username: string, brand: string): Promise<UsernameAvailabilityResponse> {
  if (!username || username.trim().length < 1) return { available: false }
  const url = new URL(`${serverUrl}${API}/account/username/availability`)
  url.searchParams.set('username', username)

  const response = await fetch(url.toString(), {
    method: 'GET',
    headers: {
      accept: 'application/json',
      brand,
    },
  })

  if (!response.ok) {
    try {
      const contentType = response.headers.get('content-type') || ''
      const parsed = contentType.includes('application/json') ? await response.json() : await response.text()
      const norm = normalizeApiError(parsed)
      // On unknown endpoint or 404, default to not blocking the user
      if (response.status === 404) return { available: true }
      throw new Error(norm.message || `HTTP ${response.status}`)
    } catch (_) {
      // Network/parse issues → treat as unknown but non-blocking
      return { available: true }
    }
  }

  const data = (await response.json()) as Partial<UsernameAvailabilityResponse>
  return { available: Boolean((data as any)?.available), suggestions: data?.suggestions || [] }
}
